import type { DateTime } from 'luxon';
import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../../content/hooks';
import type {
  ClientModel,
  Order,
  VenueModel,
  VisitGlassWasherReportData,
  VisitGlassWasherSortingFields
} from '../../models';

interface IReportsContext {
  loading: boolean;
  setLoading: (val: boolean) => void;
  dataUpdate: boolean;
  toggleUpdate: () => void;
  data: VisitGlassWasherReportData[];
  setData: (val: VisitGlassWasherReportData[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: VisitGlassWasherSortingFields;
  setOrderBy: (val: VisitGlassWasherSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: VisitGlassWasherSortingFields) => void;
  count: number;
  setCount: (val: number) => void;
  fromDate: DateTime | null;
  setFromDate: (val: DateTime | null) => void;
  toDate: DateTime | null;
  setToDate: (val: DateTime | null) => void;
  client: ClientModel | null;
  setClient: (val: ClientModel | null) => void;
  venues: VenueModel[];
  setVenues: (val: VenueModel[]) => void;
}

export const VisitWashersContext = createContext<IReportsContext>({} as IReportsContext);

export const VisitWashersProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [dataUpdate, setDataUpdate] = useState(false);
  const toggleUpdate = (): void => setDataUpdate(!dataUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<VisitGlassWasherSortingFields>(toggleUpdate, 'dateOfVisit', 'desc');

  const [data, setData] = useState<VisitGlassWasherReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState<DateTime | null>(null);
  const [toDate, setToDate] = useState<DateTime | null>(null);
  const [client, setClient] = useState<ClientModel | null>(null);
  const [venues, setVenues] = useState<VenueModel[]>([]);

  return (
    <VisitWashersContext.Provider
      value={{
        data,
        setData,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading,
        dataUpdate,
        toggleUpdate,
        fromDate,
        setFromDate,
        toDate,
        setToDate,
        client,
        setClient,
        venues,
        setVenues
      }}
    >
      {children}
    </VisitWashersContext.Provider>
  );
};
